import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Title, Text, Button, Paper, Stack, Loader } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { getOrder } from '@/lib/orders';
import * as analytics from '@/lib/analytics';
import { useCart } from '@/context/CartContext';
import { Order } from '@/types';
import { useLanguage } from '@/context/LanguageContext';

export default function OrderConfirmationPage() {
  const params = useParams();
  const id = params?.id as string;
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const { clearCart } = useCart();
  const { t } = useLanguage();

  useEffect(() => {
    if (id) {
      getOrder(id)
        .then(orderData => {
          if (orderData) {
            setOrder(orderData);
            
            // Track purchase event
            analytics.purchase({
              transaction_id: id,
              value: orderData.total,
              currency: 'PLN',
              items: orderData.items.map(item => ({
                item_id: item.product.id,
                item_name: item.product.name,
                price: item.product.price,
                quantity: item.quantity,
              })),
            });
            
            // Clear cart after successful purchase
            clearCart();
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [id, clearCart]);

  if (loading) {
    return (
      <Container size="sm" py="xl">
        <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" radius="md" withBorder>
        <Stack align="center" gap="md">
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#51cf66',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem'
          }}>
            <IconCheck size={48} color="white" />
          </div>
          
          <Title order={1} ta="center">{t('orderConfirmation.title')}</Title>
          
          <Text ta="center" c="dimmed" size="lg">
            {t('orderConfirmation.thankYou')}
          </Text>
          
          {id && (
            <Paper p="md" withBorder style={{ width: '100%' }}>
              <Text ta="center">
                {t('orderConfirmation.orderId')} <strong>{id}</strong>
              </Text>
              {order && (
                <Text ta="center" mt="xs">
                  {t('orderConfirmation.total')} <strong>{order.total.toFixed(2)} {t('common.pln')}</strong>
                </Text>
              )}
            </Paper>
          )}
          
          <Text ta="center" c="dimmed" size="sm">
            {t('orderConfirmation.emailConfirmation')}
          </Text>
          
          <Button component={Link} to="/" fullWidth mt="md" size="lg">
            {t('orderConfirmation.continueShopping')}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

